/**
 * Configuration Propagation Helpers
 *
 * Pure functions that map a configuration changeType to its propagation mode,
 * mirroring the SIP engine's RuntimeConfigRefreshService.shouldTriggerRuntimeRefresh
 * logic (packages/sip-engine/src/runtime/RuntimeConfigRefreshService.ts).
 *
 * The engine restarts gracefully for runtime-relevant change types
 * (certificate, classification_rule, host, logging, network, routing, sip_config, sip_interface,
 * full_reload). Peer and codec changes are picked up via in-process cache TTL
 * (~60 s) without a restart.
 */

/** Change types that trigger a graceful engine restart. */
export const ENGINE_RESTART_CHANGE_TYPES: ReadonlySet<string> = new Set([
  'certificate',
  'classification_rule',
  'host',
  'logging',
  'network',
  'routing',
  'sip_config',
  'sip_interface',
  'full_reload',
]);

/** In-process cache TTL in seconds for peer/codec configuration. */
export const CACHE_TTL_SECONDS = 60;

/** How the engine applies a given change type. */
export type PropagationMode = 'engine-restart' | 'cache-ttl';

/**
 * Determine how a configuration change propagates to the SIP engine.
 *
 * Mirrors `shouldTriggerRuntimeRefresh` from RuntimeConfigRefreshService —
 * runtime-relevant types restart the engine; everything else propagates
 * via the ~60 s in-process cache TTL.
 */
export function getPropagationMode(changeType: string): PropagationMode {
  return ENGINE_RESTART_CHANGE_TYPES.has(changeType) ? 'engine-restart' : 'cache-ttl';
}

/**
 * Whether a cache-TTL change is still propagating (within the TTL window).
 *
 * For engine-restart types this is always false — the restart itself is the
 * propagation signal, not a time window. A future-dated timestamp (clock skew)
 * also returns false to avoid a stuck "propagating" indicator.
 */
export function isPropagating(
  changeType: string,
  changedAt: string,
  now: number = Date.now(),
): boolean {
  if (getPropagationMode(changeType) !== 'cache-ttl') {
    return false;
  }
  const elapsedMs = now - new Date(changedAt).getTime();
  return elapsedMs >= 0 && elapsedMs < CACHE_TTL_SECONDS * 1000;
}
